import { useEffect, useState } from "react"

import { AIModel } from "@llumiverse/core"
import { ExecutionEnvironment, Interaction } from "@vertesia/common"
import { ArrowUp } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { GeneratedForm } from "@components/form/GeneratedForm"
import { AgentModelSelection } from "@components/selections/AgentModelSelection"

import { useCompositeState } from "@hooks/use-composite-state"

import FormState from "@plugins/form/FormState"

import { useUserSession } from "@session/UserSession"

import { ScrollArea } from "@ui/scroll-area"

interface ServicePayloadPanelProps {
    interaction: Interaction | undefined
    onInteractionStarted: () => void
}
export function ServicePayloadPanel({ interaction, onInteractionStarted }: Readonly<ServicePayloadPanelProps>) {
    const { client } = useUserSession()
    const playgroundState = useCompositeState(FormState)
    const payload = playgroundState.payload
    const [environment, setEnvironment] = useState<ExecutionEnvironment | undefined>(undefined)
    const [formKey] = useState(0)

    const [currentModel, setCurrentModel] = useState<AIModel | undefined>(undefined)

    useEffect(() => {
        if (!client || !interaction) {
            return
        }
        client.environments.retrieve(interaction.environment as string).then(setEnvironment)
    }, [client, interaction])

    useEffect(() => {
        if (!environment) {
            return
        }

        const newConfig = {
            environment: currentModel?.environment ?? environment.id,
            model: currentModel?.id ?? interaction?.model,
        }

        const currentConfig = playgroundState.config
        if (!currentConfig || currentConfig.environment !== newConfig.environment || currentConfig.model !== newConfig.model) {
            playgroundState.config = newConfig
        }
    }, [environment, currentModel, interaction?.model, playgroundState])

    if (!interaction) {
        return null
    }

    const run = () => {
        playgroundState
            .run()
            .catch((error) => {
                toast.error("Error Running Service", {
                    description: error.message,
                    duration: 4000,
                })
            })
            .finally(() => {
                onInteractionStarted()
            })
    }

    return (
        <div className="w-full h-full overflow-hidden flex flex-col">
            <div className="w-full h-full flex flex-col max-w-4xl mx-auto items-center justify-center px-4 pt-8 pb-4 gap-4">
                <div className="flex flex-col items-center w-full bg-white border border-gray-300 rounded-3xl p-4 gap-2">
                    <ScrollArea className="w-full max-h-[350px] overflow-hidden transition-all duration-500 flex-1 px-3 pt-3">
                        <GeneratedForm key={formKey} object={payload} components={{}} />
                    </ScrollArea>
                    <div className="flex items-center justify-end w-full mt-4 px-2 pb-2">
                        <div className="flex items-center justify-between w-full gap-2">
                            <AgentModelSelection model={interaction?.model} onChange={setCurrentModel} />
                            <ActionTooltipButton disabled={!interaction} icon={<ArrowUp />} variant="icon" size="icon" tooltip="Start Service" onClick={run} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
